import { combineReducers } from 'redux'
import { handleActions } from 'redux-actions'
import { selectorForSlice, setState, unsetState } from 'lp-redux-utils'
import {
	reducer as productsReducer,
	reducerKey as productsReducerKey,
} from './products'
import {
	reducer as newSearchFlowReducer,
	reducerKey as newSearchFlowReducerKey,
} from './new-search-flow'
import {
	reducer as companyReducer,
	reducerKey as companyReducerKey,
} from './company'
import {
	reducer as automaticRolloversReducer,
	reducerKey as automaticRolloversReducerKey,
} from './automatic-rollovers'
import {
	reducer as benefitDistributionsReducer,
	reducerKey as benefitDistributionsReducerKey,
} from './benefit-distributions'
import {
	reducer as notificationsReducer,
	reducerKey as notificationsReducerKey,
} from './notifications'
import {
	reducer as profileReducer,
	reducerKey as profileReducerKey,
} from './profile'
import * as apiActions from 'api-actions'
import { setOnSuccess } from 'lp-redux-api'
import { createSelector } from 'reselect'
import * as actions from './actions'
import {
	RetirementServicesPortalProfile,
	isSearchServicesEnabled,
	isBenefitDistributionsEnabled,
	isNotificationsEnabled,
} from 'config/portal'
import {
	ProductType,
	ServiceAgreementMapping,
	ALWAYS_ENABLED_PRODUCTS,
	ORDERED_PRODUCTS,
	AgreementType,
	AVAILABLE_STATUSES,
	AgreementStatus,
} from 'config/products'
import { sortBy, difference } from 'lodash'

const reducerKey = 'root'
const slice = 'root.global'

const initialState = {
	isAuthenticated: false,
	supportModalShown: false,
	showIdleModal: false,
	successModalMessage: null,
}
// handle actions that affect global state
const globalReducer = handleActions(
	{
		//handlers for setting different states based on dispatched actions
		[actions.setSupportModalShown]: setState('supportModalShown'),
		[actions.setShowIdleModal]: setState('showIdleModal'),
		[actions.showSuccessModal]: setState('successModalMessage'),
		[actions.hideSuccessModal]: unsetState('successModalMessage'),
		//handlers for API actions to set state on successful data fetch
		[apiActions.fetchCompanyOwner]: setOnSuccess('companyOwner'),
		[apiActions.fetchUserProfile]: setOnSuccess(
			'currentUser',
			({ payload: { data } }) => {
				//transform user profile data to include permissions based on account audience
				return {
					...data,
					permissions: {
						[data.accountAudience]: true,
					},
				}
			},
		),
		[apiActions.fetchServiceAgreements]: setOnSuccess('serviceAgreements'),
	},
	initialState,
)
//combine all individual reducers into a single root reducer
const rootReducer = combineReducers({
	[productsReducerKey]: productsReducer,
	[newSearchFlowReducerKey]: newSearchFlowReducer,
	[companyReducerKey]: companyReducer,
	[automaticRolloversReducerKey]: automaticRolloversReducer,
	[benefitDistributionsReducerKey]: benefitDistributionsReducer,
	[profileReducerKey]: profileReducer,
	[benefitDistributionsReducerKey]: benefitDistributionsReducer,
	[notificationsReducerKey]: notificationsReducer,
	global: globalReducer,
})

const select = selectorForSlice(slice)
//selectors for accessing various parts of the state managed by the global reducer
const selectors = {
	isAuthenticated: select('isAuthenticated'),
	companyOwner: select('companyOwner'),
	currentUser: select('currentUser'),
	supportModalShown: select('supportModalShown'),
	showIdleModal: select('showIdleModal'),
	serviceAgreements: select('serviceAgreements'),
	successModalMessage: select('successModalMessage'),
}
//get the accountID from the currentUser data
selectors.accountID = createSelector([selectors.currentUser], (currentUser) => {
	return currentUser?.accountID
})
//get permissions from the currentUser data
selectors.permissions = createSelector(
	[selectors.currentUser],
	(currentUser) => {
		if (!currentUser) return {}
		return currentUser.permissions
	},
)
//check if the current user has admin privilleges
selectors.isAdmin = createSelector([selectors.currentUser], (currentUser) => {
	return (
		currentUser?.retirementServicesPortalProfile ===
		RetirementServicesPortalProfile.ADMIN
	)
})

// an array of (non-overlapping) products/services the user has subscribed to
// e.g., [ { type: 'Search Services', status: 'Active' } ]  -- plus optional attributes from service agreement
selectors.orderedSubscribedProducts = createSelector(
	[selectors.serviceAgreements],
	function (serviceAgreements) {
		if (!serviceAgreements) return

		// manually create an array of product objects for always enabled products
		const alwaysEnabledProducts = ALWAYS_ENABLED_PRODUCTS.map(
			(productType) => ({
				productType,
				agreementStatus: AgreementStatus.ACTIVE,
			}),
		)
		const allServiceAgreements = alwaysEnabledProducts.concat(serviceAgreements)
		//reduce the combined list to unique products with relevant details
		const products = allServiceAgreements.reduce(
			(products, serviceAgreement) => {
				const productType =
					serviceAgreement.productType ||
					ServiceAgreementMapping[serviceAgreement.agreementType]
				// it's possible for an agreement type to not have a matching product (e.g., Direct Rollover)
				if (!productType) return products
				if (
					productType === ProductType.SEARCH_SERVICES &&
					!isSearchServicesEnabled()
				)
					return products
				if (
					productType === ProductType.BENEFIT_DISTRIBUTIONS &&
					!isBenefitDistributionsEnabled()
				)
					return products
				if (
					productType === ProductType.NOTIFICATIONS &&
					!isNotificationsEnabled()
				)
					return products
				// do not add the same product more than once:
				if (products.find((product) => product.type === productType))
					return products

				const product = {
					type: productType,
					status: serviceAgreement.agreementStatus,
					agreementId: serviceAgreement.serviceAgreementsID,
					signerName: serviceAgreement.customerSignedName,
				}
				return [...products, product]
			},
			[],
		)
		//sort the products based on a predefined order
		return sortBy(products, (product) => ORDERED_PRODUCTS.indexOf(product.type))
	},
)

// a list of product types the user is not subscribed to (i.e., no service agreement)+
// note: unlike orderedSubscribedProducts, this is an array of strings: e.g., [ "Uncashed Checks" ]
selectors.orderedNonSubscribedProductTypes = createSelector(
	[selectors.orderedSubscribedProducts],
	function (subscribedProducts) {
		return difference(
			ORDERED_PRODUCTS,
			subscribedProducts.map((product) => product.type),
		)
	},
)

// a user has access to a product if 1) there is a service agreement (= subscription) and
// 2) the agreement status is one of the following: 'Active', 'Accepted', 'Negative Consent', 'Signed'
selectors.hasAccessToProduct = createSelector(
	[selectors.orderedSubscribedProducts, (_, productType) => productType],
	(subscribedProducts, productType) => {
		if (!subscribedProducts) return

		const product = subscribedProducts.find(
			(product) => product.type === productType,
		)
		if (!product) return false

		return AVAILABLE_STATUSES.includes(product.status)
	},
)
//selector to check if there is an active search services agreement
selectors.hasSearchServiceAgreement = createSelector(
	[selectors.serviceAgreements],
	(serviceAgreements) => {
		if (serviceAgreements) {
			return serviceAgreements.some(
				(agreement) =>
					agreement.agreementType === AgreementType.SEARCH &&
					agreement.agreementStatus === AgreementStatus.ACTIVE,
			)
		}
		return false
	},
)

function reducer(state, action) {
	if (action.type === actions.resetState.toString()) {
		//reset the entire state to initial values
		return rootReducer(undefined, action)
	}
	//use rootReducer to handle other actions
	return rootReducer(state, action)
}

export { reducer, selectors, reducerKey }
